/** @jsx jsx */

import { useContext, useRef } from 'react';
import { css, jsx } from '@emotion/core';
import { useSelector } from 'react-redux';
import EditMarker from 'components/UI/EditMarker';
import { colors } from 'styles/variables';
import setCurrentBlock from 'services/apiHelpers/setCurrentBlock';
import { State } from 'store/store';
import { InteractionContext } from '../../Layouts/ContainerLogical';
import { ModuleContext } from 'components/Module/Module_v2';
import {
  IgetBaseButtonStyles,
  getBaseButtonStyles,
} from './getBaseButtonStyles';
import {
  updateLinkColors,
  parseStylesFromPropsAndTheme,
  updateButtonHeightForIE,
} from './buttonHelpers';
import hasValidLocationUpdated from 'services/hasValidLocationUpdated';
import { moduleNavigationActionsList } from 'components/NavigationSettings/ModuleNavigation';

const { lPink } = colors;

const IsExternalLink = ({ color = '#fff' }) => {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path
        d="M17 4.30213V13.6979C17 15.5021 15.533 17 13.6908 17H4.30917C2.50107 17 1 15.5362 1 13.6979V4.30213C1 2.49787 2.46695 1 4.30917 1H13.7249"
        style={{
          stroke: color,
          strokeWidth: 1.5,
          strokeMiterlimit: 10,
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
        }}
      />
      <path
        d="M11.8145 6.17447L6.18549 11.7915"
        style={{
          stroke: color,
          strokeWidth: 1.5,
          strokeMiterlimit: 10,
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
        }}
      />
      <path
        d="M6.76538 6.17447H11.8144V11.2128"
        style={{
          stroke: color,
          strokeWidth: 1.5,
          strokeMiterlimit: 10,
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
        }}
      />
    </svg>
  );
};

const NavigationButton = props => {
  //
  const moduleContext = useContext(ModuleContext);

  const interactionContext = useContext(InteractionContext) as any;

  const focusedNodeId = useSelector((state: State) => state.focusedNodeId);
  const theme = useSelector((state: State) => state.theme);
  const interactionsLocked = useSelector(
    (state: State) => state.interactionsLocked
  );

  let isLocked = interactionsLocked.includes(interactionContext.id) ? 1 : 0;

  if (props.unlocked) {
    isLocked = 0;
  }

  if (
    window?.mode &&
    window?.mode === 'PREVIEW' &&
    theme.disableLockDownInPreview
  ) {
    isLocked = 0;
  }

  if (
    !window.dev() &&
    window.location.toString().match('localhost:') &&
    theme.disableLockDownInPreview
  ) {
    isLocked = 0;
  }

  const { id, label, location } = props;

  const buttonRef = useRef<HTMLAnchorElement>(null);

  // might remove the defaults from here
  // declare any defaults somewhere else

  const linkData = {
    ...props.linkData,
    ...(location && {
      url: location,
      title: '',
      target: location.match('http') ? '_blank' : '_self',
    }),
  };

  //

  const {
    color,
    background,
    fontFamily,
    buttonShape,
    borderRadius,
    buttonShadow,
    buttonOutline,
    hoverColor,
    useLargeButton,
    focusColor,
  } = parseStylesFromPropsAndTheme(props, theme);

  const baseButtonStyles = getBaseButtonStyles({
    color,
    background,
    fontFamily,
    buttonShape,
    borderRadius,
    buttonShadow,
    buttonOutline,
    hoverColor,
    useLargeButton,
    type: 'navigation',
  }) as any;

  const { url, title, target } = linkData;

  // error checking here against a potentially removed topic
  let hasValidLocation =
    moduleNavigationActionsList.includes(url) || hasValidLocationUpdated(url);

  const selected = id === focusedNodeId;
  const { exit, setComplete, setCompleteAndExit } = moduleContext;

  const navigationButton = (
    <a
      ref={buttonRef}
      id={`navigation-button-${id}`}
      href={url}
      css={css({
        position: 'relative',

        ...baseButtonStyles,
        textDecoration: 'none',
        ...updateLinkColors(color),

        transition: 'all 0.15s',

        ...(target === '_blank' && {
          padding: '5px 40px',
        }),

        ...(!window.dev() && isLocked
          ? {
              // potentially want to be able to customize disabled color ?
              pointerEvents: 'none',
              opacity: 0.25,
            }
          : {
              ':focus': {
                boxShadow: focusColor,
              },
            }),
      })}
      title={linkData.title || url}
      onKeyUp={e => {
        if (e.keyCode === 32) {
          e.currentTarget.click();
        }
      }}
      onMouseOver={e => {
        e.currentTarget.setAttribute('title', '');
      }}
      onMouseOut={e => {
        e.currentTarget.setAttribute('title', linkData.title || url);
      }}
      onClick={e => {
        e.preventDefault();

        if (url === 'exit') {
          exit();
          return;
        }

        if (url === 'setComplete') {
          setComplete();
          return;
        }

        if (url === 'setCompleteAndExit') {
          setCompleteAndExit();
          return;
        }

        if (target === '_blank') {
          window.open(url);
          return;
        }

        interactionContext.next(url);
      }}
    >
      {label}

      {buttonShape !== 'circle' && target === '_blank' && (
        <div
          css={css({
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            right: 18,
            top: 0,
            height: '100%',
          })}
        >
          <IsExternalLink color={color} />
        </div>
      )}
    </a>
  );

  updateButtonHeightForIE(buttonRef.current, buttonShape);

  return window.dev() ? (
    <div
      css={css({
        position: 'relative',
        width: 'auto !important',
        margin: '2px 7px',

        ':hover': {
          a: {
            background: hoverColor,
            border: `1px solid ${hoverColor || background}`,
          },
        },
      })}
    >
      {navigationButton}

      <div
        css={css({
          position: 'absolute',
          left: -5,
          top: -0,
          width: 'calc(100% + 10px)',
          height: 'calc(100% + 0px)',
          boxShadow: selected
            ? `0 0 0 1px ${lPink}`
            : `0 0 0 1px ${hasValidLocation ? 'lightGrey' : 'yellow'}`,
          transition: 'all 0.15s',
          cursor: 'pointer',

          ':hover': {
            boxShadow: `0 0 0 1px ${lPink}`,
          },
        })}
        onClick={e => {
          if (selected) {
            return;
          }

          setCurrentBlock({ id });
        }}
      >
        {!selected && <EditMarker />}
      </div>
    </div>
  ) : (
    navigationButton
  );
};

export { NavigationButton };
